import { createSlice } from "@reduxjs/toolkit";

import {
    normalizeCacheId,
    readDetailCache,
    writeDetailCache,
} from "../../../helpers/redux/detailCache";
import {
    getExcistTrackersRedux,
    getExcistTrackersWithFilterRedux,
    getSingleTrackerRedux,
} from "./trackers.asyncActions";

const initialState = {
    trackers: [],
    status: "idle",
    trackersWithFilter: [],
    trackersWithFilterStatus: "idle",
    singleTracker: {},
    singleTrackerResponseMessage: null,
    isChecked: true,
    singleTrackerStatus: "idle",
    // Id of the tracker the detail page is currently interested in.
    // Responses for any other id are dropped (stale-response guard).
    activeTrackerId: null,
    // { [id]: { data, fetchedAt } } - used to hydrate singleTracker instantly
    // on page enter while a fresh request is in flight.
    trackerCacheById: {},
};

const isActiveTracker = (state, id) =>
    normalizeCacheId(id) === state.activeTrackerId;

/**
 * Makes `id` the active tracker and syncs the current slot with it: cached
 * data if we have it, otherwise an empty slot so the page never shows a
 * previously viewed tracker while the new one is loading.
 */
const activateTracker = (state, id) => {
    const normalizedId = normalizeCacheId(id);
    state.activeTrackerId = normalizedId;
    const cached = readDetailCache(state.trackerCacheById, normalizedId);
    state.singleTracker = cached?.data ?? {};
    state.singleTrackerResponseMessage = null;
};

export const trackersSlice = createSlice({
    name: "allTrackersState",
    initialState,
    reducers: {
        clearState: (state) => {
            state.singleTrackerResponseMessage = null;
            state.singleTracker = {};
            state.activeTrackerId = null;
            return state;
        },
        clearTrackerCache: (state) => {
            state.trackerCacheById = {};
        },
        hydrateSingleTrackerFromCache: (state, action) => {
            activateTracker(state, action.payload);
        },
        setSingleTrackerName: (state, action) => {
            if (state.singleTracker?.tracker) {
                state.singleTracker.tracker.tracker_name = action.payload;
            }
            const cached = readDetailCache(
                state.trackerCacheById,
                state.activeTrackerId,
            );
            if (cached?.data?.tracker) {
                cached.data.tracker.tracker_name = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getExcistTrackersRedux.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getExcistTrackersRedux.fulfilled, (state, action) => {
                state.trackers = action.payload.data && action.payload.data;
                state.isChecked = false;
                state.status = "success";
            })
            .addCase(getExcistTrackersRedux.rejected, (state) => {
                state.status = "failed";
            })
            .addCase(getExcistTrackersWithFilterRedux.pending, (state) => {
                state.trackersWithFilterStatus = "loading";
            })
            .addCase(
                getExcistTrackersWithFilterRedux.fulfilled,
                (state, action) => {
                    state.trackersWithFilterStatus = "success";
                    state.trackersWithFilter =
                        action.payload.data && action.payload.data;
                },
            )
            .addCase(getExcistTrackersWithFilterRedux.rejected, (state) => {
                state.trackersWithFilterStatus = "failed";
            })
            .addCase(getSingleTrackerRedux.pending, (state, action) => {
                // If the page didn't hydrate first (different id than the one
                // currently active), swap the slot ourselves so stale data from
                // another tracker is never shown under the loader.
                if (!isActiveTracker(state, action.meta.arg)) {
                    activateTracker(state, action.meta.arg);
                }
                state.singleTrackerStatus = "loading";
                state.singleTrackerResponseMessage = null;
            })
            .addCase(getSingleTrackerRedux.fulfilled, (state, action) => {
                if (!isActiveTracker(state, action.meta.arg)) {
                    return;
                }
                const data = action.payload.data;
                state.singleTrackerStatus = "success";
                state.singleTracker = data;
                if (data) {
                    writeDetailCache(
                        state.trackerCacheById,
                        action.meta.arg,
                        data,
                    );
                }
            })
            .addCase(getSingleTrackerRedux.rejected, (state, action) => {
                if (
                    action.meta.aborted ||
                    !isActiveTracker(state, action.meta.arg)
                ) {
                    return;
                }
                state.singleTrackerStatus = "failed";
                state.singleTrackerResponseMessage =
                    action.payload?.message ?? action.error?.message ?? null;
            });
    },
});

export default trackersSlice.reducer;
export const {
    clearState,
    clearTrackerCache,
    hydrateSingleTrackerFromCache,
    setSingleTrackerName,
} = trackersSlice.actions;
